import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import * as _ from 'lodash';
import * as moment from 'moment';
import { takeUntil } from 'rxjs';
import { BaseComponent } from './../../../../core/components/base/base.component';
import { FeedbackService } from './../../../../core/services/feedback.service';
import { MorbidityMortalityService } from './../../../../core/services/morbidity-mortality.service';
import { PatientService } from './../../../../core/services/patient.service';

@Component({
  selector: 'app-morbidity-mortality-list',
  templateUrl: './morbidity-mortality-list.component.html',
  styleUrls: ['./morbidity-mortality-list.component.scss']
})
export class MorbidityMortalityListComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() filterValue: string = '';
  filteredPatients: any[] = [];
  allPatients: any[] = [];

  constructor(
    private patientService: PatientService,
    private morbidityMortalityService: MorbidityMortalityService,
    private router: Router,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);
  }

  ngOnInit() {
    this.morbidityMortalityService.morbidityMortality$
      .pipe(takeUntil(this.destroy$))
      .subscribe((results: any) => {
        this.allPatients = results;
        this.applyFilter();
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['filterValue']) {
      this.applyFilter();
    }
  }

  buildDiagnosisString(patient: any): string {
    const { diagnosis2, diagnosis3, diagnosis4, diagnosis5 } = patient;
    const diagnoses = [diagnosis2, diagnosis3, diagnosis4, diagnosis5].filter(Boolean); // Filter out falsy values
    const diagnosisString = diagnoses.join(', ');
    return diagnosisString.length > 70 ? `${diagnosisString.substring(0, 70)}...` : diagnosisString;
  }

  getMorbidityMortalityType(item: any): string {
    if (item.isMortality) {
      return 'Mortality';
    } else if (item.isMorbidity) {
      return 'Morbidity';
    }
    return 'Unknown';
  }

  getMorbidityMortalityIcon(item: any): string {
    if (item.isMortality) {
      return 'warning';
    } else if (item.isMorbidity) {
      return 'info';
    }
    return 'help';
  }

  applyFilter() {
    if (this.filterValue.trim() === '') {
      this.filteredPatients = this.groupAndSort(this.allPatients);
    } else {
      const filterTerms = this.filterValue.toLowerCase().split(' ');
      const filteredData = this.allPatients.filter(item => {

        const icdCodesMatch = item.Patient.icdCodes?.some((icdCode: any) =>
          filterTerms.every(term =>
            _.includes((icdCode.code || '').toLowerCase(), term) ||
            _.includes((icdCode.description || '').toLowerCase(), term)
          )
        );

        const patientMatch = filterTerms.every(term =>
          _.includes(item.Patient.name.toLowerCase(), term) ||
          _.includes(item.Patient.surname.toLowerCase(), term) ||
          _.includes(item.Patient.patientNumber?.toLowerCase(), term) ||
          _.includes(item.Patient.diagnosis?.toLowerCase(), term) ||
          _.includes(item.title?.toLowerCase(), term) ||
          _.includes(item.notes?.toLowerCase(), term)
        );

        return icdCodesMatch || patientMatch;
      });
      this.filteredPatients = this.groupAndSort(filteredData);
    }
  }

  private groupAndSort(data: any[]) {
    // Group notes by formatted date (no need to format date here since we're just grouping)
    const grouped = _.groupBy(data, item => item.date ? moment(item.date).format('YYYY-MM-DD') : moment(item.createdAt).format('YYYY-MM-DD'));

    // Sort the groups by date in descending order and transform into an array
    return _.chain(grouped)
      .map((value, key) => ({ date: key, items: _.orderBy(value, ['Patient.surname', 'Patient.name'], ['asc', 'asc']) }))
      .orderBy(group => group.date, 'desc')
      .value();
  }


  navigateToPatientFile(patientId: string) {
    this.patientService.getPatientById(patientId)
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(result => {
        this.router.navigate(['/patients', {
          outlets: {
            patient: ['file', patientId],
          }
        }]);
      }, error => {
        console.error(error);
        this.showToastMessage('There was an error loading the patient file. Please try again.');
      });
  }
}

